/**
 * API service integrations for the Salzburg Cab Ski Transfers pages.
 * Implements real network requests to the backend server with standard headers and error handling.
 * Fallback local mock data is provided with a simple switch to enable/disable.
 */

export interface SkiTransfer {
  id: string;
  title: string;
  sourceNumber?: string;
  sourceName?: string;
  subtitle: string;
  shortDescription: string;
  time: string; // e.g. '1h 30m'
  base_price: number;
  image: string;
  images: string[];
  tags: string[];
  highlights: string[];
  excludedFeatures?: string[];
  excluded_features?: string[];
  goodToKnow: string[];
 

  overview?: string;
  categories?: string[];
  routeQuery?: string;
  startLocation?: string;
  maxPeople?: number;
  active?: boolean;
  showOnHomepage?: boolean;
  itinerary?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  warning?: string;
}

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://72.61.92.71:8000').replace(/\/$/, '');

export const USE_REAL_BACKEND = true;

export function shouldShowOnHomepage(item?: any): boolean {
  const value =
    item?.showOnHomepage ??
    item?.show_on_homepage ??
    item?.showOnHomePage;

  const showOnHomepage = typeof value === 'boolean'
    ? value
    : typeof value === 'number'
      ? value === 1
      : typeof value === 'string'
        ? ['true', '1', 'yes'].includes(value.trim().toLowerCase())
        : false;
  const activeValue = item?.active ?? item?.is_active;
  const active = typeof activeValue === 'boolean'
    ? activeValue
    : typeof activeValue === 'number'
      ? activeValue === 1
      : typeof activeValue === 'string'
        ? ['true', '1', 'yes', 'active'].includes(activeValue.trim().toLowerCase())
        : true;

  return active && showOnHomepage;
}

/** Normalize backend/mock payloads so subtitle + shortDescription always exist (day-trip parity). */
export function normalizeSkiTransfer(
  raw: Partial<SkiTransfer> | Record<string, unknown>,
): SkiTransfer {
  const data = raw as Record<string, unknown>

  const parseBooleanValue = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()

      if (['true', '1', 'yes'].includes(normalized)) return true
      if (['false', '0', 'no'].includes(normalized)) return false
    }

    return undefined
  }

  const parseArrayField = (val: unknown): string[] => {
    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === 'string') return item
          if (!item) return ''

          if (typeof item === 'object') {
            const objectItem = item as Record<string, unknown>

            return (
              objectItem.name ??
              objectItem.title ??
              objectItem.label ??
              JSON.stringify(item)
            )
          }

          return String(item)
        })
        .map((s) => s && String(s).trim())
        .filter(Boolean)
    }

    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val)

        if (Array.isArray(parsed)) {
          return parseArrayField(parsed)
        }

        return val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      } catch (e) {
        return val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
    }

    return []
  }

  const categories = parseArrayField(
    data.categories ??
    data.category_list ??
    data.tags
  )

  let tags = parseArrayField(
    data.tags ??
    data.categories ??
    data.category_list
  )

  if (tags.length === 0 && categories.length > 0) {
    tags = categories
  }

  // Debugging: log payload and parsed arrays
  // eslint-disable-next-line no-console
  console.debug(
    '[normalizeSkiTransfer] id=',
    data.id,
    'raw.categories=',
    data.categories,
    'raw.tags=',
    data.tags,
    'parsed.categories=',
    categories,
    'parsed.tags=',
    tags
  )

  const priceRaw =
    data.price ??
    data.basePrice ??
    data.base_price ??
    data.extraPrice ??
    data.extra_price ??
    480

  const priceNum =
    typeof priceRaw === 'number'
      ? priceRaw
      : (Number(priceRaw) || 480)

  const images =
    Array.isArray(data.images)
      ? data.images
          .map((img: unknown) =>
            typeof img === 'string'
              ? img
              : (img as Record<string, unknown>)?.image
          )
          .filter(Boolean)
      : typeof data.image_urls === 'string'
        ? parseArrayField(data.image_urls)
        : Array.isArray(data.image_urls)
          ? parseArrayField(data.image_urls)
          : Array.isArray(data.gallery_images)
            ? data.gallery_images
                .map((item: unknown) =>
                  typeof item === 'string'
                    ? item
                    : (item as Record<string, unknown>)?.image
                )
                .filter(Boolean)
            : []

  const title =
    data.title ||
    'Ski Transfer'

  const subtitle =
    data.subtitle ||
    data.tagline ||
    data.location ||
    ''

  const shortDescription =
    data.shortDescription ??
    data.short_description ??
    data.description ??
    ''

  const overview =
    data.overview ??
    data.shortDescription ??
    data.short_description ??
    data.description ??
    ''
  const itinerary = data.itinerary ?? '';
  const time =
    data.time ??
    data.duration ??
    '�'

  const singleImage =
    data.image ??
    data.cover_image ??
    data.thumbnail ??
    (images[0] || '/assets/Skytranfers/skycard1.png')

  const routeQuery =
    data.routeQuery ??
    data.route_query ??
    data.location ??
    ''

  const startLocation =
    data.startLocation ??
    data.start_location ??
    ''

  const maxPeople =
    Number(
      data.maxPeople ??
      data.max_people ??
      8
    )

  const highlights =
    parseArrayField(
      data.highlights ??
      data.highlights_list ??
      data.features
    )

  const excludedFeatures =
    parseArrayField(
      data.excludedFeatures ??
      data.excluded_features ??
      data.excluded_features_rel
    )

  const goodToKnow =
    parseArrayField(
      data.goodToKnow ??
      data.good_to_know
    )

  const active =
    parseBooleanValue(data.active) ??
    parseBooleanValue(data.is_active) ??
    true

  const showOnHomepage =
    parseBooleanValue(data.showOnHomepage) ??
    parseBooleanValue(data.show_on_homepage) ??
    false

  return {
    id: String(data.id ?? ''),
    title: String(title),
    sourceNumber: String(
      data.sourceNumber ??
      data.source_number ??
      ''
    ),

    sourceName: String(
      data.sourceName ??
      data.source_name ??
      ''
    ),
    subtitle: String(subtitle),
    shortDescription: String(shortDescription),
    time: String(time),
    base_price: priceNum,

    image: String(
      singleImage ||
      '/assets/Skytranfers/skycard1.png'
    ),

    images:
      images.length > 0
        ? images as string[]
        : [
            String(
              data.image ??
              data.cover_image ??
              data.thumbnail ??
              '/assets/Skytranfers/skycard1.png'
            )
          ],

    tags,

    highlights,

    excludedFeatures,

    excluded_features: excludedFeatures,

    goodToKnow,

    overview: String(overview),

    itinerary: String(itinerary),

    categories,

    routeQuery: String(routeQuery),

    startLocation: String(startLocation),

    maxPeople,

    active,

    showOnHomepage,
  }
}

export async function apiGetSkiTransfers(): Promise<ApiResponse<SkiTransfer[]>> {
  try {
    const url = `${API_BASE_URL}/api/admin/ski-transfers/`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    let result = null

    try {
      result = await response.json()
    } catch (err) {
      // keep result null
    }

    // Debug: log network info to browser console for easier troubleshooting
    // eslint-disable-next-line no-console
    console.debug(
      '[apiGetSkiTransfers] url=',
      url,
      'status=',
      response.status,
      'ok=',
      response.ok,
      'result=',
      result
    )

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to fetch ski transfers.'
      };
    }

    const rawData = Array.isArray(result?.results)
      ? result.results
      : result?.data || result

    const data = Array.isArray(rawData)
      ? rawData.map(normalizeSkiTransfer)
      : []

    return {
      success: true,
      message: result?.message || 'Ski transfers loaded successfully.',
      data,
    };
  } catch (error) {
    return {
      success: false,
      message: 'Failed to connect to ski transfers service.',
      data: [],
    };
  }
}

export async function apiGetSkiTransferById(id: string): Promise<ApiResponse<SkiTransfer>> {
  try {
    const url = `${API_BASE_URL}/api/admin/ski-transfers/${id}/`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json().catch(() => null);

    if (!response.ok || !result) {
      return {
        success: false,
        message: result?.message || result?.detail || `Failed to fetch transfer with id ${id}.`
      };
    }

    return {
      success: true,
      message: result?.message || 'Transfer details loaded successfully.',
      data: normalizeSkiTransfer(result?.data || result),
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.'
    };
  }
}